import React, {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
} from 'react';

import { SnackbarKey, SnackbarProvider, useSnackbar } from 'notistack';

import Snackbar from './Snackbar';

// Todo: change arguments to options object as one argument

type ContextStateType = {
  showSnackbar(
    message: ReactElement | string,
    type?: 'success' | 'error' | 'info' | 'report',
    autoHideDuration?: number | null,
    optionalMessage?: ReactElement | string,
    onClose?: () => void,
    onSubmit?: () => void,
    onView?: () => void,
    isFullWidth?: boolean,
    isReportButton?: boolean,
  ): void;
};

const SnackbarContext = createContext<ContextStateType>({
  showSnackbar: () => {},
});

type SnackBarContextProviderProps = {
  children?: React.ReactNode;
};

const SnackBarContextProvider: FC<SnackBarContextProviderProps> = ({
  children,
}) => (
  <SnackbarProvider maxSnack={3}>
    <SnackBarContext>{children}</SnackBarContext>
  </SnackbarProvider>
);

type SnackBarContextProps = {
  children?: React.ReactNode;
};

const SnackBarContext: FC<SnackBarContextProps> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleCloseSnackbar = useCallback((key: SnackbarKey): void => {
    closeSnackbar(key);
  }, []);

  const showSnackbar = useCallback(
    (
      message: ReactElement | string,
      type: 'error' | 'success' | 'info' | 'report' = 'error',
      autoHideDuration = 3000,
      optionalMessage: ReactElement | string,
      onClose: () => void,
      onSubmit: () => void,
      onView: () => void,
      isFullWidth = false,
      isReportButton = false,
    ) => {
      enqueueSnackbar(null, {
        autoHideDuration,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        content: (key) => (
          <Snackbar
            message={message}
            optionalMessage={optionalMessage}
            type={type}
            handleClose={(): void => {
              onClose && onClose();
              handleCloseSnackbar(key);
            }}
            handleView={onView}
            handleSubmit={onSubmit}
            isFullWidth={isFullWidth}
            isReportButton={isReportButton}
          />
        ),
      });
    },
    [],
  );

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
      }}
    >
      {children}
    </SnackbarContext.Provider>
  );
};

const useSnackBarContext = (): ContextStateType => {
  const snackbarContext = useContext<ContextStateType>(SnackbarContext);

  if (!snackbarContext) {
    throw Error(
      'useSnackBarContext hook should be wrapped by SnackBarContextProvider',
    );
  }

  return snackbarContext;
};

export { SnackBarContextProvider, useSnackBarContext };
